import { cobraConstants } from "../cobra/cobra-constants";
import { formatters } from "../framework";
import { formatRate, sortCoverageLevels, getStartDateFromDateRange, getEndDateFromDateRange, getLatestDateRangeFromRates } from "./cobra-coverage-level-helpers.js";

const state = {
  ready: false,
  employerId: null,
  plans: [],
  planTypes: [],
  rateBandTypes: [],
  serviceTypes: [],
  dependentTypes: []
};

const getters = {
  allPlans: (state) => {
    return (state.plans.length > 0) ? state.plans : [];
  },
  cobraPlans: (state) => {
    return (state.plans.length > 0) ? state.plans.filter(p => p.cobraEligible) : [];
  },
  getPlan: (state) => (planId) => {
    return planId ? state.plans.find(p => p.planId == planId) : '';
  },
  getPlanType: (state) => (planTypeId) => {
    return planTypeId ? state.planTypes.find(pt => pt.planTypeId == planTypeId).name : '';
  },
  getRateType: (state, getters) => (planId) => {
    let plan = getters.getPlan(planId);
    return plan && plan.rateType ? Object.values(cobraConstants.RATE_TYPES).find(type => type.id == plan.rateType) : null;
  },
  getRateBandType: (state, getters) => (planId) => {
    let rateType = getters.getRateType(planId);
    let plan = getters.getPlan(planId);
    return (plan && rateType == cobraConstants.RATE_TYPES.Banded) ? state.rateBandTypes.find(rbt => rbt.id == plan.rateBandType) : null;
  },
  getCoverageLevels: (state) => (planId) => {
    if (!(planId && state.plans.find(p => p.planId == planId))) return [];
    return state.plans.find(p => p.planId == planId).coverageLevels || [];
  },
  getRateSummary: (state, getters) => (planId) => {
    let coverageLevels = getters.getCoverageLevels(planId);
    let maxDateRange = getters.getLatestRateDateRange(planId);
    if (coverageLevels.length === 0 || maxDateRange == null) return 'None';

    let rateBandType = getters.getRateBandType(planId);
    if (rateBandType) return 'Banded: ' + rateBandType.description;

    coverageLevels = coverageLevels.filter(cl => cl.rates.some(r => r.dateRange == maxDateRange));

    let rateSummary = coverageLevels.map(cl =>
      cl.name + ': ' + getters.getRateFormattedById(cl.coverageLevelId, cl.rates.find(r => r.dateRange == maxDateRange).rateId)
    ).join(', ');
    return (rateSummary.includes('Age-Banded')) ? 'Age-Banded' : rateSummary;
  },
  getDependentRequirements: (state) => (coverageLevelId) => {
    return coverageLevelId ? state.plans.map(p => p.coverageLevels).flat().find(cl => cl.coverageLevelId == coverageLevelId).dependentRequirements : [];
  },
  getCoverageLevel: (state) => (coverageLevelId) => {
    return coverageLevelId ?
      state.plans.map(p => p.coverageLevels).flat().find(cl => cl.coverageLevelId === coverageLevelId) :
      null;
  },
  getCoverageLevelName: (state) => (coverageLevelId) => {
    return coverageLevelId ?
      state.plans.map(p => p.coverageLevels).flat().find(cl => cl.coverageLevelId === coverageLevelId).name :
      '';
  },
  getLatestRateDateRange: (state, getters) => (planId) => {
    let coverageLevels = getters.getCoverageLevels(planId);
    if (coverageLevels.length === 0) return null;
    return getLatestDateRangeFromRates(coverageLevels.flatMap(cl => cl.rates));
  },
  getPlanDateRanges: (state, getters) => (planId) => {
    let plan = planId ? state.plans.find(r => r.planId == planId) : null;
    if (!plan || !plan.coverageLevels || plan.coverageLevels.length === 0) return [];

    let dateRanges = [...new Set(plan.coverageLevels.flatMap(cl => cl.rates).map(r => r.dateRange))];
    dateRanges = dateRanges.map(range => ({
      id: range, name: range, startDate: moment(getStartDateFromDateRange(range)), endDate: getEndDateFromDateRange(range) ? moment(getEndDateFromDateRange(range)) : null
    }));
    dateRanges.sort((a, b) => b.startDate.isAfter(a.startDate) ? 1 : -1);
    return dateRanges;
  },
  getRateFormattedById: (state, getters) => (coverageLevelId, rateId) => {
    let coverageLevel = getters.getCoverageLevel(coverageLevelId);
    if (!coverageLevel || !coverageLevel.rates) return null;
    let rate = coverageLevel.rates.find(r => r.rateId == rateId);
    if (!rate || !rate.rateBands || rate.rateBands.length === 0) return null;

    return formatRate(rate);
  },
};

const actions = {
  async loadPlans(context, data) {
    if (this.employerId == data.employerId && state.plans.length > 0) return;
    this.employerId = data.employerId;
    await context.dispatch("getDataFromApi", { employerId: data.employerId });
  },
  async refreshPlans(context, data) {
    await context.dispatch("getDataFromApi", { employerId: data.employerId });
  },
  async getDataFromApi(context, data) {
    if (!data.employerId) return;
    var loadPromises = [
      cobraApi.get(`/employer/${data.employerId}/plans`),
      cobraApi.get(`/plan-types`),
      cobraApi.get(`/employer/${data.employerId}/service-types`),
      cobraApi.get(`/employer/${data.employerId}/coverage-levels`),
      cobraApi.get(`/employer/${data.employerId}/rates`),
      cobraApi.get(`/employer/${data.employerId}/person-types`),
      cobraApi.get(`/employer/${data.employerId}/plan-contacts`),
      cobraApi.get(`/employer/${data.employerId}/plan-locations`),
      cobraApi.get(`/employer/${data.employerId}/rate-band/types`),
      cobraApi.get(`/employer/${data.employerId}/dependent-types`)
    ];

    let [plans, planTypes, serviceTypes, coverageLevels, rates, personTypes, planContacts, planLocations, rateBandTypes, dependentTypes] = await Promise.all(loadPromises);

    let coverageLevelsByPlanId = _.groupBy(coverageLevels, (cl) => cl.planId);
    let personTypesByCoverageLevelId = _.groupBy(personTypes, (pt) => pt.coverageLevelId);
    let ratesByCoverageLevelId = _.groupBy(rates, (r) => r.coverageLevelId);
    let planContactsByPlanId = _.groupBy(planContacts, (pc) => pc.planId);
    let planLocationsByPlanId = _.groupBy(planLocations, (pl) => pl.planId);

    plans.forEach(p => {
      p.planType = planTypes.find(pt => pt.planTypeId === p.planTypeId);
      p.coverageLevels = coverageLevelsByPlanId[p.planId] || [];
      p.planContacts = planContactsByPlanId[p.planId] || [];
      p.planLocations = planLocationsByPlanId[p.planId] || [];

      p.coverageLevels.forEach(cl => {
        cl.rates = ratesByCoverageLevelId[cl.coverageLevelId] || [];
        cl.rates.forEach(r => {
          r.dateRange = `${formatters.date(r.startDate)} - ${r.endDate ? formatters.date(r.endDate) : 'Open'}`;
          if (r.rateBands) r.rateBands.forEach(rb => {
            if (!rb) return;
            if (parseFloat(rb.coverageAmount)) rb.coverageAmount = parseFloat(rb.coverageAmount).toFixed(4).toString();
            if (parseFloat(rb.total)) rb.total = parseFloat(rb.total).toFixed(4).toString();
            if (parseFloat(rb.employerPays)) rb.employerPays = parseFloat(rb.employerPays).toFixed(4).toString();
          });
        });

        let coverageLevelPersonTypes = personTypesByCoverageLevelId[cl.coverageLevelId] || [];
        cl.personTypes = coverageLevelPersonTypes;
        cl.dependentRequirements = coverageLevelPersonTypes.map(pt => {
          let personType = cobraConstants.PERSON_TYPES.find(p => p.id === pt.personType);
          let personDependentTypes = dependentTypes.filter(dt => dt.personTypes.includes(personType.id));

          return {
            header: personType.name,
            types: personDependentTypes.map(dt => dt.id),
            min: pt.min,
            max: pt.max
          }
        });
      });

      p.coverageLevels = sortCoverageLevels(p.coverageLevels);
    });

    context.commit('setData', { plans: plans, planTypes: planTypes, serviceTypes: serviceTypes, rateBandTypes: rateBandTypes, dependentTypes: dependentTypes });
  },
  async updatePlan(context, data) {
    await cobraApi.put(`/employer/${data.employerId}/plan/${data.planId}`, data);
    await context.dispatch("refreshPlans", { employerId: data.employerId });
  },
  async createPlan(context, data) {
    const plan = {...data, 'active': true};
    let planId = await cobraApi.post(`/employer/${data.employerId}/plan`, plan);
    await context.dispatch("refreshPlans", { employerId: data.employerId });
    return planId;
  },
  async setCoverageLevels(context, data) {
    await cobraApi.post(`employer/${data.employerId}/plan/${data.planId}/coverage-levels`, data.coverageLevels);
    await context.dispatch("refreshPlans", { employerId: data.employerId });
  },
  async addOrUpdatePlanContact(context, data) {
    await cobraApi.post(`employer/${data.employerId}/plan/${data.planId}/plan-contact`, data.planContact);
    await context.dispatch("refreshPlans", { employerId: data.employerId });
  },
  async deletePlanContact(context, data) {
    await cobraApi.delete(`employer/${data.employerId}/plan/${data.planId}/plan-contact/${data.planContactId}`);
    await context.dispatch("refreshPlans", { employerId: data.employerId });
  }
};
const mutations = {
  setData(state, data) {
    utils.copyCommonProperties(data, state);
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};



// WEBPACK FOOTER //
// ./src/newCobra/x-benefits-plans.js